import { useForm, Controller } from "react-hook-form";
import { TipoColaboradorEnum } from "../../../enums/tipoColaborador";
import { useCallback, useEffect, useState } from "react";
import { maskCpf, maskPhone } from "../../../utils/masks";
import { prettyNamePerfil, removeUnderscore } from "../../../utils/removeUnderscore";
import { z } from "zod";
import { ISelectOptions } from "../../../types/inputs";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "react-router-dom";
import { Colaborador } from "../../../services/Colaborador";
import { useContextSite } from "../../../context/Context";
import { toast } from "react-toastify";
import { Loja } from "../../../services/Lojas";
import { Delivery } from "../../../services/Delivery";
import { Perfil } from "../../../services/Perfil";

const tipoOptions = Object.values(TipoColaboradorEnum).map((e) => ({
  value: e,
  label: removeUnderscore(e),
}));

const schemaOption = z.object({
  value: z.string(),
  label: z.string(),
});

const schema = z.object({
  cpf: z.string().min(14, "CPF invalido"),
  nome: z.string().min(1, "Campo obrigatorio"),
  email: z.string().email("Email invalido").optional().or(z.literal("")),
  telefone: z
    .string()
    .optional()
    .refine(
      (val) => !val || [10, 11].includes(val.replace(/\D/g, "").length),
      { message: "Telefone invalido" }
    ),
  tipo: z.string().min(1, "Campo obrigatorio"),
  uuidDeliverys: z.array(schemaOption).optional(),
  uuidLojas: z.array(schemaOption).optional(),
  perfis: z.array(schemaOption, { message: "Campo obrigatorio" }),
  ativo: z.boolean(),
});

export type FormCollaboratorEditValues = z.infer<typeof schema>;

export const useFormCollaboratorEdit = () => {
  const [perfis, setPerfis] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [lojas, setLojas] = useState<ISelectOptions[]>([] as ISelectOptions[]);
  const [deliverys, setDeliverys] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [params] = useSearchParams();
  const id = params.get("id");
  const { setIsLoad } = useContextSite();

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormCollaboratorEditValues>({
    defaultValues: {
      cpf: "",
      nome: "",
      email: "",
      telefone: "",
      uuidLojas: [],
      uuidDeliverys: [],
      tipo: "" as TipoColaboradorEnum,
      perfis: [],
      ativo: true,
    },
    resolver: zodResolver(schema),
    mode: "onSubmit",
  });

  const getPerfis = useCallback(() => {
    Perfil.get({incluirAdmin: false, incluirInativos: false}).then(({ data }) => {
      const options = data?.content.map((i) => ({
        value: i?.nome,
        label: removeUnderscore(prettyNamePerfil(i?.nome)),
        element: i,
      }));

      setPerfis(options);
    });
  }, []);

  useEffect(() => {
    const currentValue = watch("telefone");
    const newPhoneValue = maskPhone(currentValue);
    if (currentValue !== newPhoneValue) {
      setValue("telefone", newPhoneValue, { shouldValidate: true });
    }
  }, [watch("telefone"), setValue]);

  const getLojas = useCallback(() => {
    Loja.get()
      .then(({ data }) => {
        const options = data?.content?.map((i) => ({
          value: i?.uuid,
          label: i?.nome,
          element: i,
        }));
        setLojas(options);
      })
      .catch(
        ({
          response: {
            data: { messagem },
          },
        }) => toast.error(messagem)
      );
  }, []);

  const getDeliverys = useCallback(() => {
    Delivery.get()
      .then(({ data }) => {
        const options = data?.content?.map((i) => ({
          value: i?.uuid,
          label: i?.cidade,
          element: i,
        }));
        setDeliverys(options);
      })
      .catch(
        ({
          response: {
            data: { messagem },
          },
        }) => toast.error(messagem)
      );
  }, []);

  useEffect(() => {
    getPerfis();
    getLojas();
    getDeliverys();
  }, []);

  function getColaborador() {
    setIsLoad(true);

    Colaborador.getById({ uuid: id })
      .then(({ data }) => {
        const perfisOptions = data?.perfis?.map((i) => ({
          value: i?.nome,
          label: removeUnderscore(i?.nome),
        }));
        setValue("nome", data?.nome || "");
        setValue("cpf", maskCpf(data?.cpf) || "");
        setValue("telefone", data?.telefone || "");
        setValue("email", data?.email || "");
        setValue("tipo", TipoColaboradorEnum[data?.tipo] || "");
        setValue("perfis", perfisOptions || []);
        setValue(
          "uuidLojas",
          data?.lojas?.map((i) => ({ value: i?.uuid, label: i?.nome })) || []
        );
        setValue(
          "uuidDeliverys",
          data?.deliverys?.map((i) => ({ value: i?.uuid, label: i?.cidade })) ||
            []
        );
        setValue("ativo", data?.ativo);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      )
      .finally(() => {
        setIsLoad(false);
      });
  }

  useEffect(() => {
    if (id) {
      getColaborador();
    }
  }, [id]);

  return {
    handleSubmit,
    register,
    control,
    perfis,
    errors,
    tipoOptions,
    Controller,
    lojas,
    deliverys,
  };
};
